import React from "react";
import { GlassCard } from "../components/GlassCard";
import { Tag } from "../components/Tag";
import { SectionLabel } from "../components/SectionLabel";

export const AboutPage = ({ c, isDark }) => {
  const bgDot = isDark
    ? "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)"
    : "radial-gradient(rgba(0,0,50,0.06) 1px, transparent 1px)";

  const philosophyCards = [
    { icon: "layers", color: c.primary, title: "Scalability", desc: "Designing distributed systems that handle horizontal growth. Thinking in microservices, statelessness, and eventual consistency." },
    { icon: "terminal", color: c.secondary, title: "DX", desc: "Developer experience is not a luxury. I prioritize clean APIs, robust documentation, and automated CI/CD pipelines to empower teams." },
    { icon: "speed", color: c.onSecondaryContainer, title: "Performance", desc: "Every millisecond counts. Optimization through meticulous profiling, efficient algorithms, and low-latency data access patterns." },
  ];

  const timeline = [
    { period: "2022 — PRESENT", color: c.secondary, role: "Senior Systems Architect", desc: "Leading the core infrastructure team to rebuild the real-time data ingestion engine processing 1.2M events/sec.", tags: ["Rust", "Kubernetes", "gRPC"], img: "https://lh3.googleusercontent.com/aida-public/AB6AXuBmHklJ0PcErGgXvjWDCbWACuFoUKAPD_nUYYGEdRr5IG2zK_4WBGq7Og20JtW0c35oNE3pk0IIy3P5x_cYa65EMHERSe8CFl5UQop4QI_9BGGcQ0dvERiBUuUrcHHfSFSxORXuh6KSXpIqErtpSJyKvoNVWSeWEJ3AHRHdFKtY0YMDft0EgUUwPTgh0nFVmoRMF2XYC0VVDfvLrGJoy-lPTzgHA0hN3pxbclUHYp3MDEP1sWOITTRVbv4O0tWJkLhHjvdi8Ci_HH8" },
    { period: "2020 — 2022", color: c.primary, role: "Full Stack Lead", desc: "Architected and shipped a multi-tenant SaaS platform for fintech analytics. Reduced infrastructure costs by 40% through serverless migration.", tags: ["Next.js", "AWS Lambda", "PostgreSQL"], img: "https://lh3.googleusercontent.com/aida-public/AB6AXuAG0x9x6GK62wHhAOr1AxW8D87V4G2P1Fh5zTUTm9Rvagy_W_rCXF0ZJFLi4Z7cUu-4WHGs7irHHwN6d5NDASIAUfUv8mhMSM02pelDLRUzstmwB5wVtA0KYZnA5KCg-a16E_EeXHhVPWtJZGHbu-pYvnNYsnAWjmkvQbR-Bxg0HOCX40RgelSvwIKQYP3yx4RvEmqaI4A0kmKc5L8Y34zj6VyLhLgOqzSs42kDQ00VuxXR51RB1xfoIUuIytQzIlwGM_MfMvXA3tM" },
    { period: "2018 — 2020", color: c.secondary, role: "Software Engineer II", desc: "Developed core features for a collaborative design tool. Optimized canvas rendering using WebGL and WebWorkers.", tags: ["TypeScript", "React", "WebAssembly"], img: "https://lh3.googleusercontent.com/aida-public/AB6AXuCvJNzJ-JtqtFf4Xa5j0jON5p7mJc0uYtkrAQVFFBs8E6_lG4xAh5a4Dnh-rRdar8HHHzgN9g8kmc_X30PGDTZrRBJjJoXS_8AHU5FYnNHsaenVGX7jr7ZkLB4hZAp07IyDenqkFvjFcBgRBPqfoRhCSlWyzamQmdrZkkwqYCoB2Pi8XCeupGelQY-qnz6KTHQlMynVZdhoNdYrb3vv-mtmtILgURebSOUHDdd0OoMXVEouCCMlLFK_vy6R79ZIecdkWYtdQpmXiHM" },
  ];

  return (
    <div style={{ background: c.background, minHeight: "100vh", backgroundImage: bgDot, backgroundSize: "32px 32px", paddingTop: 120 }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 64px 120px" }}>
        <header style={{ marginBottom: 80 }}>
          <div style={{ display: "grid", gridTemplateColumns: "8fr 4fr", alignItems: "flex-end", gap: 32 }}>
            <div>
              <SectionLabel c={c}>Architecture & Execution</SectionLabel>
              <h1 style={{ fontFamily: "'Geist', sans-serif", fontWeight: 700, fontSize: "clamp(36px, 5vw, 64px)", color: c.onSurface, lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 16 }}>
                Engineering Philosophy
              </h1>
              <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 18, color: c.onSurfaceVariant, maxWidth: 560, lineHeight: 1.6 }}>
                I believe the best software is built at the intersection of pragmatism and precision—systems that are both elegant and resilient.
              </p>
            </div>
          </div>
        </header>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32, marginBottom: 80 }}>
          {philosophyCards.map(({ icon, color, title, desc }) => (
            <GlassCard key={title} isDark={isDark} style={{ borderRadius: 16, padding: 32, cursor: "pointer", transition: "all 0.5s" }}>
              <div style={{ marginBottom: 16 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 40, color }}>{icon}</span>
              </div>
              <h3 style={{ fontFamily: "'Geist', sans-serif", fontSize: 22, fontWeight: 600, color: c.onSurface, marginBottom: 12 }}>{title}</h3>
              <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 16, color: c.onSurfaceVariant, lineHeight: 1.6 }}>{desc}</p>
            </GlassCard>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "4fr 8fr", gap: 32 }}>
          <div style={{ position: "sticky", top: 100, alignSelf: "flex-start" }}>
            <h2 style={{ fontFamily: "'Geist', sans-serif", fontSize: 28, fontWeight: 600, color: c.onSurface, marginBottom: 16 }}>Experience</h2>
            <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 16, color: c.onSurfaceVariant, marginBottom: 32, lineHeight: 1.6 }}>
              A timeline of technical leadership and full-stack execution across high-growth environments.
            </p>
            <GlassCard isDark={isDark} style={{ borderRadius: 12, padding: 16, borderColor: `${c.primary}30` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.primary, animation: "pulse 2s infinite" }} />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: c.primary, letterSpacing: "0.05em" }}>CURRENT STATUS</span>
              </div>
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: c.onSurface }}>Senior Systems Architect @ NeuraLink Systems</p>
            </GlassCard>
          </div>

          <div style={{ position: "relative" }}>
            <div style={{
              position: "absolute", left: "50%", top: 0, bottom: 0, width: 1,
              background: `linear-gradient(to bottom, ${c.primary}, ${c.secondaryContainer})`,
              opacity: 0.2, transform: "translateX(-50%)",
            }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 80 }}>
              {timeline.map((item, i) => (
                <div key={i} style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "center" }}>
                  <div style={{
                    position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)",
                    width: 16, height: 16, borderRadius: "50%", background: c.background,
                    border: `2px solid ${item.color}`, zIndex: 1,
                    boxShadow: `0 0 15px ${item.color}80`,
                  }} />
                  <div style={{ textAlign: "right", paddingRight: 32 }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: item.color, display: "block", marginBottom: 8 }}>{item.period}</span>
                    <h3 style={{ fontFamily: "'Geist', sans-serif", fontSize: 22, fontWeight: 600, color: c.onSurface, marginBottom: 12 }}>{item.role}</h3>
                    <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 15, color: c.onSurfaceVariant, lineHeight: 1.6, marginBottom: 12 }}>{item.desc}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "flex-end" }}>
                      {item.tags.map(t => <Tag key={t} c={c} isDark={isDark}>{t}</Tag>)}
                    </div>
                  </div>
                  <div style={{ paddingLeft: 32 }}>
                    <img src={item.img} alt={item.role} style={{
                      width: "100%", aspectRatio: "16/9", objectFit: "cover",
                      borderRadius: 12, opacity: 0.6, filter: "grayscale(1)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                      transition: "all 0.5s",
                    }}
                      onMouseEnter={e => { e.target.style.filter = "none"; e.target.style.opacity = 1; }}
                      onMouseLeave={e => { e.target.style.filter = "grayscale(1)"; e.target.style.opacity = 0.6; }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};